import type { ClientFile } from "@repo/shared/types";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./file-processing-state.i18n";

// The Summary cell of a file row: the AI summary once processing is done,
// otherwise the queue / transcribing / failed state, and `fallback` (e.g. the
// file size) for a processed or skipped file that has no summary.
export function FileProcessingState({ file, fallback }: { file: ClientFile; fallback: ReactNode }) {
  const t = useT(T);

  if (file.processingStatus === "pending" || file.processingStatus === "processing") {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin shrink-0" />
        {file.processingStatus === "processing" ? t.statusProcessing : t.statusPending}
      </span>
    );
  }

  if (file.processingStatus === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 text-destructive">
        <AlertCircle className="h-3 w-3 shrink-0" />
        <span className="truncate">
          {t.statusFailed}
          {file.processingError ? `: ${file.processingError}` : ""}
        </span>
      </span>
    );
  }

  if (file.processingStatus === "done" && file.summary) {
    return (
      <span className="inline-flex items-center gap-1.5 max-w-full">
        <Sparkles className="h-3 w-3 shrink-0 text-primary" />
        <span className="truncate">{file.summary}</span>
      </span>
    );
  }

  return <>{fallback}</>;
}
